import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../api.service';
import { CookieService } from 'ngx-cookie-service';
import { CommonService } from "../../shared/common.service";
import { HttpParams } from '@angular/common/http';

interface User {
  id: string;
  first_name: string;
  last_name: string;
  mobile: string;
  email: string;
  role: string;
  designation: string;
  site_location: string;
  site_city: string;
  site_state: string;
  site_country: string;
  site_address: string;
  status: string;
  name: string;
}

@Component({
  selector: 'app-subscriptions',
  templateUrl: './subscriptions.component.html',
  styleUrls: ['./subscriptions.component.scss']
})
export class SubscriptionsComponent implements OnInit {
  users: User[] = [];
  filteredData: User[] = [];
  paginatedData: User[] = [];
  searchText = '';
  searchSite = '';
  roleFilter = '';
  designationFilter = '';
  statusFilter = '';
  totalPages: number[] = [];
  currentPage = 1;
  pageSize = 10;  
  pages = 0; 
  totalUsers = 0; 
  data: User[] = [];
  authToken: string | null = null;
  previousPage: number;
  pageNumbersToShow: number = 4;
  pageRange: number[] = [];
  maxPagesToShow: number = 4;
  loading: boolean = false;

  constructor(
    private apiService: ApiService, 
    private router: Router,
    private cookieService: CookieService, 
    private commonService: CommonService,
    private route: ActivatedRoute 
  ) {}

  ngOnInit(): void {
    let userData = this.commonService.getUserData(); 

    if (userData == null || userData.designation != 'CRO') {
      this.commonService.navigateTo("/");
    }

    this.authToken = this.cookieService.get('token');
    console.log('Retrieved Token:', this.authToken);

    let params = new HttpParams();
    this.route.queryParams.subscribe(params => {
      this.currentPage = +params['page'] || 1;
      this.searchText = params['searchText'] || '';
      this.searchSite = params['searchSite'] || '';
      this.designationFilter = params['designationFilter'] || '';
      this.statusFilter = params['statusFilter'] || '';
      this.loadUsers(this.currentPage);
    });

    this.searchUsers();
  }

  searchUsers(): void {
    this.updateQueryParams();
    this.applyFilters();
  }

  loadUsers(page: number): void {
    this.loading = true;

    const searchText = this.searchText || '';
    const searchSite = this.searchSite || '';
    const designationFilter = this.designationFilter || '';
    const statusFilter = this.statusFilter || '';
    const userData = this.commonService.getUserData();

    if (!userData || !userData.id) {
      this.loading = false;
      return;
    }

    this.apiService.getRegisteredUserList(page, this.pageSize, searchText, searchSite, designationFilter, statusFilter, userData.id).subscribe(
      (response: any) => {
        this.users = response.data;
        this.data = response.data;

        this.users = this.data.map(user => ({
          ...user,
          name: `${user.first_name} ${user.last_name}`,
          site_address: `${user.site_city}, ${user.site_state}, ${user.site_country}`,
          status: this.extractStatus(user.status)
        }));

        this.filteredData = [...this.users];
        this.pages = Math.ceil(this.filteredData.length / this.pageSize);
        this.totalUsers = this.filteredData.length;
        this.updatePageRange();
        this.loading = false;
      },
      error => {
        console.error('Error fetching users', error);
        this.loading = false;
      }
    );
  }


  updateQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        page: this.currentPage,
        searchText: this.searchText,
        searchSite: this.searchSite,
        designationFilter: this.designationFilter,
        statusFilter: this.statusFilter
      },
      queryParamsHandling: 'merge'
    });
  }

  updatePageRange(): void {
    this.pageRange = [];

    const startPage = Math.floor((this.currentPage - 1) / this.maxPagesToShow) * this.maxPagesToShow + 1;
    const endPage = Math.min(startPage + this.maxPagesToShow - 1, this.pages);

    for (let i = startPage; i <= endPage; i++) {
      this.pageRange.push(i);
    }
  }

  nextPage(): void {
    if (this.currentPage < this.pages) {
      this.currentPage++;
      this.updatePageRange();
      this.loadUsers(this.currentPage);
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePageRange();
      this.loadUsers(this.currentPage);
    }
  }

  goToPage(pageIndex: number): void {
    if (pageIndex > 0 && pageIndex <= this.pages) {
      this.currentPage = pageIndex;
      this.updatePageRange();
      this.loadUsers(this.currentPage);
    }
  }



  extractStatus(status: string): string {
    if (status === 'Active') return 'Active';
    if (status === 'Inactive') return 'Inactive';
    return 'Unknown';
  }

  applyFilters(): void {
    if (!this.users || this.users.length === 0) {
      this.filteredData = [];
      return;
    }

    this.filteredData = this.users.filter(user => {
      const matchesSearchText = !this.searchText || 
        (user.name?.toLowerCase().includes(this.searchText.toLowerCase()) ||
         user.mobile?.includes(this.searchText));

      const matchesDesignation = !this.designationFilter || 
        user.designation === this.designationFilter;

      const matchesStatus = !this.statusFilter || 
        user.status === this.statusFilter;

      const matchesSite = !this.searchSite || 
        (user.site_location?.toLowerCase().includes(this.searchSite.toLowerCase()));

      return matchesSearchText && matchesDesignation && matchesStatus && matchesSite;
    });

    this.pages = Math.ceil(this.filteredData.length / this.pageSize);
    this.totalUsers = this.filteredData.length;
    this.currentPage = 1;
    this.updatePageRange();
    this.updatePaginatedData();
  }
  
  calculateTotalPages(): void {
    const pageCount = Math.ceil(this.filteredData.length / this.pageSize);
    this.totalPages = Array.from({ length: pageCount }, (_, i) => i);
  }

  updatePaginatedData(): void {
    const start = this.currentPage * this.pageSize;
    const end = start + this.pageSize;
    this.paginatedData = this.filteredData.slice(start, end);
  }


  editUser(userId: string, currentPage: number): void {
    this.router.navigate([`/edit-user/${userId}`], { 
      queryParams: {
        page: currentPage,
        searchText: this.searchText,
        searchSite: this.searchSite,
        designationFilter: this.designationFilter,
        statusFilter: this.statusFilter
      }
    });
  }
  
  viewUser(userId: string, currentPage: number): void {
    this.router.navigate([`/edit-user/${userId}`], { 
      queryParams: {
        page: currentPage,
        searchText: this.searchText,
        searchSite: this.searchSite,
        designationFilter: this.designationFilter,
        statusFilter: this.statusFilter
      }
    });
  }

  clear(): void {
    this.searchText = '';
    this.searchSite = '';
    this.designationFilter = '';
    this.statusFilter = '';

    this.filteredData = [...this.users];
    this.pages = Math.ceil(this.filteredData.length / this.pageSize);
    this.totalUsers = this.filteredData.length;
    this.currentPage = 1;
    this.updatePageRange();
    this.updatePaginatedData();
  }

  disableUser(id: number, isActive: boolean): void {
    console.log('Received ID:', id);
    console.log('Received is_active:', isActive);

    if (!id) {
      console.error('User ID is undefined');
      return;
    }

    const payload = { is_active: isActive ? 1 : 0 };
    this.apiService.post(`api/deactivateUser/${id}`, payload).subscribe(
      (response) => {
        console.log('User status updated:', response);
        this.loadUsers(this.currentPage);
      },
      (error) => {
        console.error('Error updating user status:', error);
        window.alert('Failed to update user status.');
      }
    );
  }
 
  toggleUserStatus(user: User): void {
    const newStatus = user.status === 'Active' ? 'inactive' : 'active';

    this.apiService.updateStatus(user.id, newStatus).subscribe(
      response => {
        console.log('User status updated successfully', response);
        user.status = newStatus === 'active' ? 'Active' : 'Inactive';
        this.loadUsers(this.currentPage);
      },
      error => {
        console.error('Error updating user status', error);
      }
    );
  }
}